/* eslint-disable complexity */
import {Box, Typography} from '@mui/material'
import React from 'react'

import {TimeSlotComponentProps} from '../types'

/**
 * Individual time slot button component
 * Adapted from mobile app HMTimeSlot component
 */
export const TimeSlotComponent: React.FC<TimeSlotComponentProps> = ({
  value,
  isAvailable,
  isSelected,
  onSelect,
  disabled = false,
  hasInstantApproval = false
}) => {
  // Parse time value - handle potential AM/PM format
  const [time, unit] = value.split(' ')

  // Get slot style based on state
  const getSlotStyle = () => {
    if (isSelected) {
      return {
        backgroundColor: '#016AD4',
        border: '1px solid #016AD4',
        color: '#FFFFFF'
      }
    } else if (isAvailable && hasInstantApproval) {
      // Instant approval slots - green border
      return {
        backgroundColor: '#FFFFFF',
        border: '1px solid #00AB50',
        color: '#00274D'
      }
    } else if (isAvailable && !hasInstantApproval) {
      // Dispatch approval needed slots - amber border
      return {
        backgroundColor: '#FFFFFF',
        border: '1px solid #F59E0B',
        color: '#00274D'
      }
    } else {
      // Unavailable slots (out of business hours or out of lead time)
      return {
        backgroundColor: '#FFFFFF',
        border: '1px solid rgba(0,39,77,0.1)',
        color: disabled ? '#9CA3AF' : '#00274D'
      }
    }
  }

  const style = getSlotStyle()

  return (
    <Box
      onClick={() => {
        // All available slots should be clickable, regardless of instant approval status
        if (!disabled && isAvailable) {
          onSelect(value)
        }
      }}
      sx={{
        flex: '0 0 calc(16.666% - 7px)', // Fixed width, no grow/shrink - 1/6 of container width minus gap
        minWidth: '84px',
        maxWidth: '120px',
        height: '46px',
        borderRadius: '6px',
        cursor: !disabled && isAvailable ? 'pointer' : 'not-allowed',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '12px',
        boxShadow: !isSelected && !isAvailable ? '0px 2px 2px 0px rgba(0,0,0,0.08)' : 'none',
        flexShrink: 0,
        opacity: !disabled && isAvailable ? 1 : 0.6,
        ...style,
        '&:hover': {
          opacity: !disabled && isAvailable ? 0.8 : 0.6
        }
      }}
      data-test-id={`time-slot-option-${value}${isSelected ? '-selected' : ''}${
        isAvailable ? '-available' : ''
      }`}
    >
      {/* Left indicator for available/selected slots */}
      {(isAvailable || isSelected) && (
        <Box
          sx={{
            width: '5px',
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            borderTopLeftRadius: '6px',
            borderBottomLeftRadius: '6px',
            backgroundColor: isSelected
              ? '#FFFFFF'
              : hasInstantApproval
                ? '#00AB50' // Green for instant approval
                : '#F59E0B' // Amber for dispatch approval
          }}
        />
      )}

      {/* Time content */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'row'
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Inter',
            fontWeight: 500,
            fontSize: '16px',
            lineHeight: '22px',
            color: style.color
          }}
        >
          {time}
        </Typography>
        {unit && (
          <Typography
            sx={{
              fontFamily: 'Inter',
              fontWeight: 400,
              fontSize: '12px',
              lineHeight: '16px',
              color: style.color,
              marginLeft: '4px',
              alignSelf: 'flex-end'
            }}
          >
            {unit}
          </Typography>
        )}
      </Box>
    </Box>
  )
}
